import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MapPin, Clock, Phone, Search, Navigation, CheckCircle, QrCode, ExternalLink, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSelectedRelay } from '@/hooks/useSelectedRelay';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import pickupImage from '@/assets/pickup-point.jpg';
import { ContactRelayDialog } from '@/components/buyer/ContactRelayDialog';
import { useLanguage } from '@/hooks/useLanguage';
import { useCMSContent } from '@/hooks/useCMSContent';

interface Relay {
  id: string;
  name: string;
  address: string;
  contact_name: string;
  contact_phone: string;
  active: boolean;
  created_at: string;
  hours_json?: any;
  latitude?: number;
  longitude?: number;
}

const RelayPoints = () => {
  const [relays, setRelays] = useState<Relay[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedContactRelay, setSelectedContactRelay] = useState<Relay | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedRelayId, setSelectedRelayId } = useSelectedRelay();
  const highlightedRelayId = searchParams.get('highlight');
  const { language, t } = useLanguage();
  const { content: cmsContent, loading: cmsLoading } = useCMSContent('relay-points-page', language);

  const pageContent = cmsContent?.content || {};

  useEffect(() => {
    fetchRelays();
  }, []);

  useEffect(() => {
    if (highlightedRelayId && relays.length > 0) {
      // Scroll to highlighted relay after a short delay to ensure DOM is updated
      setTimeout(() => {
        const element = document.getElementById(`relay-${highlightedRelayId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [highlightedRelayId, relays]);

  const fetchRelays = async () => {
    try {
      const { data, error } = await supabase
        .from('relays')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setRelays(data || []);
    } catch (error) {
      console.error('Error fetching relays:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRelays = relays.filter(relay =>
    relay.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    relay.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRandomHours = useMemo(() => {
    const hours = [
      "Lun-Sam 8h-20h, Dim 10h-18h",
      "Lun-Ven 7h-21h, Sam-Dim 9h-19h",
      "Lun-Sam 8h-19h, Dim 10h-17h",
      "7j/7 - 24h/24",
      "Lun-Ven 9h-18h, Sam 9h-15h, Dim Fermé"
    ];
    // Create a stable mapping based on relay IDs
    const hoursMap = new Map();
    relays.forEach((relay, index) => {
      hoursMap.set(relay.id, hours[index % hours.length]);
    });
    return hoursMap;
  }, [relays]);

  const getRandomDistance = useMemo(() => {
    // Create a stable mapping based on relay IDs
    const distanceMap = new Map();
    relays.forEach((relay) => {
      // Use relay ID to generate a stable "random" distance
      const hash = relay.id.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const distance = (Math.abs(hash) % 100) / 10 + 0.5;
      distanceMap.set(relay.id, distance.toFixed(1));
    });
    return distanceMap;
  }, [relays]);

  const handleLocalize = (relay: Relay) => {
    if (relay.latitude && relay.longitude) {
      window.open(`https://www.google.com/maps?q=${relay.latitude},${relay.longitude}`, '_blank');
    } else {
      // Fallback: recherche par adresse
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(relay.address + ', Kinshasa, RDC')}`, '_blank');
    }
  };

  const handleItinerary = (relay: Relay) => {
    if (relay.latitude && relay.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${relay.latitude},${relay.longitude}`, '_blank');
    } else {
      // Fallback: recherche par adresse
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(relay.address + ', Kinshasa, RDC')}`, '_blank');
    }
  };

  const handleSelectRelay = (relayId: string) => {
    setSelectedRelayId(relayId);
    const relay = relays.find(r => r.id === relayId);
    toast({
      title: "Point relais sélectionné",
      description: `${relay?.name} sera utilisé pour vos achats actuels`,
    });
  };

  const handleContactRelay = (relay: Relay) => {
    setSelectedContactRelay(relay);
    setContactDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">{pageContent.hero_title || 'Points Relais Nazandoo'}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {pageContent.hero_description || `Trouvez le point relais le plus proche de chez vous pour retirer vos commandes 
              en toute sécurité. Plus de ${relays.length} points disponibles à Kinshasa.`}
            </p>
          </div>

          {/* Search */}
          <div className="mb-8">
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Rechercher par nom ou adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* How it works */}
            <div className="lg:col-span-1">
              <Card className="sticky top-8">
                <CardHeader>
                  <CardTitle>{pageContent.how_it_works_title || 'Comment ça marche ?'}</CardTitle>
                  <CardDescription>
                    {pageContent.how_it_works_description || 'Retirer vos commandes en 4 étapes simples'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Featured Image */}
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <img
                      src={pickupImage}
                      alt="Point relais Nazandoo"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Steps */}
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">Commandez en ligne</h4>
                        <p className="text-sm text-muted-foreground">
                          Choisissez votre point relais préféré lors de la commande
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">Recevez votre QR Code</h4>
                        <p className="text-sm text-muted-foreground">
                          Code de retrait envoyé par SMS et email
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-bold text-primary">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold">Présentez-vous au relais</h4>
                        <p className="text-sm text-muted-foreground">
                          Munissez-vous de votre QR Code et d'une pièce d'identité
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-success rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-success-foreground" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Retirez votre commande</h4>
                        <p className="text-sm text-muted-foreground">
                          Vérification du code et remise de votre colis
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button className="w-full bg-gradient-primary">
                    <QrCode className="mr-2 h-4 w-4" />
                    Scanner un QR Code
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Relay Points List */}
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-2xl font-semibold">
                  {filteredRelays.length} point{filteredRelays.length > 1 ? 's' : ''} relais trouvé{filteredRelays.length > 1 ? 's' : ''}
                </h2>
                <Button variant="outline" size="sm">
                  <Navigation className="mr-2 h-4 w-4" />
                  Localiser
                </Button>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-6">
                        <div className="space-y-3">
                          <div className="h-4 bg-muted rounded w-3/4"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                          <div className="h-3 bg-muted rounded w-2/3"></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredRelays.map((relay) => (
                    <Card 
                      key={relay.id} 
                      id={`relay-${relay.id}`}
                      className={`hover:shadow-card transition-all duration-300 ${
                        highlightedRelayId === relay.id || selectedRelayId === relay.id
                          ? 'ring-2 ring-primary shadow-lg bg-primary/5 border-primary' 
                          : ''
                      }`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-lg">{relay.name}</h3>
                              <Badge variant="default" className="bg-success">
                                Ouvert
                              </Badge>
                              {selectedRelayId === relay.id && (
                                <Badge variant="default" className="bg-primary">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Sélectionné
                                </Badge>
                              )}
                            </div>
                            
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="flex items-start space-x-2">
                                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <span>{relay.address}</span>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <Clock className="h-4 w-4 flex-shrink-0" />
                                <span>{getRandomHours.get(relay.id)}</span>
                              </div>
                              
                              {relay.contact_phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-4 w-4 flex-shrink-0" />
                                  <span>{relay.contact_phone}</span>
                                </div>
                              )}
                            </div>

                            {relay.contact_name && (
                              <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                                <p className="text-sm">
                                  <span className="font-medium">Contact :</span> {relay.contact_name}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right ml-4">
                            <div className="space-y-2">
                              <Badge variant="secondary" className="text-primary">
                                {getRandomDistance.get(relay.id)} km
                              </Badge>
                              <div className="flex flex-col space-y-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleLocalize(relay)}
                                  title="Voir sur Google Maps"
                                >
                                   <MapPin className="h-3 w-3 mr-1" />
                                  {t('relayPoints.localize')}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleItinerary(relay)}
                                  title="Obtenir l'itinéraire"
                                >
                                   <Navigation className="h-3 w-3 mr-1" />
                                  {t('relayPoints.itinerary')}
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleContactRelay(relay)}
                                  title="Contacter le responsable"
                                >
                                   <MessageCircle className="h-3 w-3 mr-1" />
                                  {t('relayPoints.contact')}
                                </Button>
                                <Button 
                                  size="sm"
                                  variant={selectedRelayId === relay.id ? "default" : "outline"}
                                  onClick={() => handleSelectRelay(relay.id)}
                                >
                                  {selectedRelayId === relay.id ? (
                                    <>
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Sélectionné
                                    </>
                                  ) : (
                                    'Choisir ce point'
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {filteredRelays.length === 0 && !loading && (
                    <Card>
                      <CardContent className="text-center py-12">
                        <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-semibold mb-2">Aucun point relais trouvé</h3>
                        <p className="text-muted-foreground">
                          Essayez de modifier votre recherche ou contactez notre support.
                        </p>
                        <Button className="mt-4" onClick={() => setSearchTerm('')}>
                          Voir tous les points relais
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      
      {selectedContactRelay && (
        <ContactRelayDialog
          open={contactDialogOpen}
          onOpenChange={setContactDialogOpen}
          relayId={selectedContactRelay.id}
          relayName={selectedContactRelay.name}
        />
      )}
    </div>
  );
};

export default RelayPoints;